// Normalizes one identifier used by the Paid requirement checks.
const read_text = (value = '') => {
    return `${value || ''}`.trim();
};

const read_business_provider = (business = {}) => {
    const provider = read_text(
        business?.provider
        || business?.metadata?.provider
        || business?.assets?.find((asset) => read_text(asset?.provider).length)?.provider
    ).toLowerCase().replace(/[\s-]+/g, '_');
    const identityKey = read_text(business?.identityKey || business?.metadata?.identityKey).toLowerCase();

    if (['google', 'google_ads'].includes(provider)
        || identityKey.startsWith('google:')
        || read_text(business?.googleCustomerId || business?.metadata?.googleCustomerId).length) {
        return 'google';
    }

    return 'meta';
};

const business_is_selected = (business = {}) => {
    const businessId = read_text(
        business?.value
        || business?.businessId
        || business?.assetSourceId
        || business?._id
        || business?.id
        || business?.metadata?.businessId
    );
    const businessName = read_text(
        business?.label
        || business?.name
        || business?.businessName
        || business?.metadata?.businessName
    ).toLowerCase();

    return businessId.length > 0
        && !['default', 'no-business'].includes(businessId.toLowerCase())
        && !['default', 'no business'].includes(businessName);
};

// Reads persisted provider selections from one SASS board card.
const read_card_ad_businesses = (offerItem = {}) => {
    return (offerItem?.niffler?.assets || []).filter((asset) => {
        const businessId = read_text(asset?.assetSourceId || asset?.businessId || asset?.metadata?.businessId).toLowerCase();
        const businessName = read_text(asset?.name || asset?.businessName || asset?.metadata?.businessName).toLowerCase();

        return read_text(asset?.type).toLowerCase() === 'dailyprofit_business'
            && businessId.length > 0
            && !['default', 'no-business'].includes(businessId)
            && !['default', 'no business'].includes(businessName);
    }).map((business) => {
        return {
            ...business,
            provider: read_business_provider(business)
        };
    });
};

// Reads whether one persisted card contains a confirmed Niffler user link.
const read_card_user_is_matched = (offerItem = {}) => {
    const username = read_text(
        offerItem?.niffler?.client?.username
        || offerItem?.client?.username
        || offerItem?.typesData?.sourceClientUsername
    ).toLowerCase();
    const userId = read_text(
        offerItem?.niffler?.client?.accountUserId
        || offerItem?.niffler?.client?.userId
        || offerItem?.client?.accountUserId
        || offerItem?.client?.userId
        || offerItem?.typesData?.sourceClientUserId
    );
    const registrationStatus = read_text(
        offerItem?.niffler?.client?.registrationStatus
        || offerItem?.client?.registrationStatus
        || offerItem?.typesData?.sourceClientRegistrationStatus
    ).toLowerCase();
    const savedUserAsset = (offerItem?.niffler?.assets || []).some((asset) => {
        return read_text(asset?.type).toLowerCase() === 'user' && read_text(asset?.userId) === userId;
    });

    return username.length > 0 && userId.length > 0 && (registrationStatus === 'registered' || savedUserAsset);
};

// Returns Paid requirements for the username and provider businesses selected in the slider.
export const read_sass_paid_selection_requirements = (usernameMatched = false, selectedProviders = [], selectedBusinesses = []) => {
    const legacyBusinessValue = typeof selectedProviders === 'string' ? selectedProviders : '';
    const providers = legacyBusinessValue.length
        ? business_is_selected({ value: legacyBusinessValue, label: legacyBusinessValue }) ? ['meta'] : []
        : [...new Set((selectedProviders || []).map((provider) => read_text(provider).toLowerCase()).filter((provider) => ['meta', 'google'].includes(provider)))];
    const businesses = legacyBusinessValue.length
        ? business_is_selected({ value: legacyBusinessValue, label: legacyBusinessValue }) ? [{ value: legacyBusinessValue, label: legacyBusinessValue, provider: 'meta' }] : []
        : (selectedBusinesses || []).filter(business_is_selected).map((business) => {
            return {
                ...business,
                provider: read_business_provider(business)
            };
        });
    const missingProviderBusinesses = providers.filter((provider) => {
        return !businesses.some((business) => business.provider === provider);
    });
    const providerBusinessesAreComplete = missingProviderBusinesses.length === 0
        && businesses.length === providers.length;
    const userRequirementLookup = {
        true: '',
        false: 'matched username'
    };
    const missingFields = [
        userRequirementLookup[usernameMatched],
        providers.length ? '' : 'ad provider',
        providers.length && !providerBusinessesAreComplete ? 'provider businesses' : ''
    ].filter(Boolean);

    return {
        valid: missingFields.length === 0,
        missingFields,
        selectedProviders: providers,
        selectedBusinesses: businesses
    };
};

// Returns Paid requirements from the user and business already stored on one card.
export const read_sass_paid_card_requirements = (offerItem = {}) => {
    const selectedBusinesses = read_card_ad_businesses(offerItem);

    return read_sass_paid_selection_requirements(
        read_card_user_is_matched(offerItem),
        selectedBusinesses.map((business) => business.provider),
        selectedBusinesses
    );
};

// Formats the same actionable Paid conflict used by the backend.
export const read_sass_paid_requirements_message = (requirements = {}) => {
    const messageLookup = {
        'matched username': 'Match a username before moving to PAID.',
        'ad provider': 'Select Meta Ads or Google Ads before moving to PAID.',
        'provider businesses': 'Select a business for every enabled ad provider before moving to PAID.',
        'matched username|ad provider': 'Match a username and select Meta Ads or Google Ads before moving to PAID.',
        'matched username|provider businesses': 'Match a username and select a business for every enabled ad provider before moving to PAID.'
    };

    return messageLookup[(requirements.missingFields || []).join('|')] || '';
};
